import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private toasts = signal<Toast[]>([]);
  private dismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
  private currentToastId: string | null = null;

  getToasts() {
    return this.toasts;
  }

  show(toast: Omit<Toast, 'id'>) {
    // Clear existing timeout to reset the auto-dismiss timer
    if (this.currentToastId) {
      const existingTimeout = this.dismissTimeouts.get(this.currentToastId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        this.dismissTimeouts.delete(this.currentToastId);
      }
    }

    const currentToasts = this.toasts();
    if (currentToasts.length > 0 && this.currentToastId) {
      this.toasts.update(toasts => 
        toasts.map(t => t.id === this.currentToastId 
          ? { ...t, title: toast.title, message: toast.message, type: toast.type } 
          : t
        )
      );
    } else {
      const id = Date.now().toString();
      this.currentToastId = id;
      const newToast: Toast = { ...toast, id };
      this.toasts.set([newToast]);
    }

    const duration = toast.duration ?? 5000;
    const timeout = setTimeout(() => this.dismiss(this.currentToastId!), duration);
    this.dismissTimeouts.set(this.currentToastId!, timeout);

    return this.currentToastId!;
  }

  success(title: string, message: string) {
    return this.show({ type: 'success', title, message });
  }

  error(title: string, message: string) {
    return this.show({ type: 'error', title, message });
  }

  warning(title: string, message: string) {
    return this.show({ type: 'warning', title, message });
  }

  info(title: string, message: string) {
    return this.show({ type: 'info', title, message });
  }

  dismiss(id: string) {
    const timeout = this.dismissTimeouts.get(id);
    if (timeout) {
      clearTimeout(timeout);
      this.dismissTimeouts.delete(id);
    }
    this.toasts.update(current => current.filter(t => t.id !== id));
    if (this.currentToastId === id) {
      this.currentToastId = null;
    }
  }

  clear() {
    this.dismissTimeouts.forEach(timeout => clearTimeout(timeout));
    this.dismissTimeouts.clear();
    this.currentToastId = null;
    this.toasts.set([]);
  }
}
